// Advance-order slots (Planlæg / "deliver later") — which ones exist, and
// which one the checkout starts on.
//
// The rule for "is the venue open at this slot" is isAvailabilityOpenAt, the
// client mirror of the backend's isRestaurantOpenNow (src/utils/openingHours.js).
// That is what /woltDelivery/check answers 409 "Restaurant is closed at the
// selected pickup time" with, so a slot offered here that the mirror calls
// closed is a slot the guest is refused at the quote.
//
// Pure functions: RestaurantMenu is too big for its hooks to be tested, so the
// decision lives here (same reasoning as utils/woltDelivery.js).

import { isAvailabilityOpenAt } from './restaurant-availability';

const MINUTE_MS = 60 * 1000;
const MINUTES_PER_DAY = 24 * 60;

const pad = (value) => String(value).padStart(2, '0');

const startOfDay = (date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

const atMinutes = (day, minutes) => {
  const moment = new Date(day);
  moment.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return moment;
};

const toMinutes = (hhmm) => {
  const [hours, minutes] = String(hhmm).split(':').map(Number);
  return Number.isFinite(hours) && Number.isFinite(minutes) ? hours * 60 + minutes : NaN;
};

export const slotLabel = (minutes) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/**
 * Is the venue open at a given slot on a given day, by the server's rule?
 *
 * @param {Object} availability - the venue's stored Mon..Sun schedule
 * @param {Date} day - any moment on the day
 * @param {string} time - "HH:mm"
 */
export const isPickupSlotOpen = (availability, day, time) => {
  const minutes = toMinutes(time);
  if (Number.isNaN(minutes)) return false;
  return isAvailabilityOpenAt(availability, atMinutes(startOfDay(day), minutes));
};

/**
 * The first slot a guest can order ahead for.
 *
 * Walks forward from `fromDate` (clamped to today), one `interval` at a time,
 * and returns the first slot that is
 *   - at least `leadMinutes` after `now` (the kitchen / courier lead time),
 *   - not in `disabledTimesFor(day)` (the picker's own list — category hours,
 *     past times), and
 *   - inside the venue's opening hours by the server's rule.
 * A day with nothing left rolls over to the next day's first open slot.
 *
 * @returns {{ date: Date, time: string }|null} `date` is the start of the day.
 *   null when nothing in the coming `maxDays` days qualifies.
 */
export const firstPickupSlot = ({
  availability,
  now = new Date(),
  leadMinutes = 0,
  interval = 10,
  fromDate = null,
  maxDays = 7,
  disabledTimesFor = null,
} = {}) => {
  const step = Number(interval) > 0 ? Number(interval) : 10;
  const earliest = now.getTime() + Math.max(0, Number(leadMinutes) || 0) * MINUTE_MS;

  const today = startOfDay(now);
  const requested = fromDate ? startOfDay(fromDate) : today;
  const day = requested.getTime() > today.getTime() ? requested : today;

  for (let offset = 0; offset <= maxDays; offset += 1) {
    const disabled = (disabledTimesFor && disabledTimesFor(new Date(day))) || [];

    for (let minutes = 0; minutes < MINUTES_PER_DAY; minutes += step) {
      const slot = atMinutes(day, minutes);
      if (slot.getTime() < earliest) continue;

      const label = slotLabel(minutes);
      if (disabled.includes(label)) continue;
      if (!isAvailabilityOpenAt(availability, slot)) continue;

      return { date: new Date(day), time: label };
    }

    day.setDate(day.getDate() + 1);
  }

  return null;
};

/**
 * Which pickup toggle a guest lands on: "Hurtigst muligt" whenever the venue is
 * open right now, "Planlæg" only when it is closed and it takes advance orders.
 * Pre-selecting "Planlæg" at an open venue sent the guest into a scheduled slot
 * nobody asked for.
 */
export const defaultPickupMode = ({ allowAdvanceOrder, openNow }) =>
  allowAdvanceOrder && !openNow ? 'later' : 'now';

const pickupSlots = { firstPickupSlot, isPickupSlotOpen, defaultPickupMode, slotLabel };

export default pickupSlots;
